/**
 * Resolves the data-fetching context for the currently-edited node:
 *   - parameter values (segments collapsed to plain strings)
 *   - the active connection reference
 *   - the connector ID
 *
 * Used by fields that drive backend dynamic-data calls (e.g. `useDynamicTree`,
 * `useConnectorDynamicList`) where the inputs are derived from the same
 * three sources. Keeps that resolution in one place so every field treats
 * "no connection yet" / `_connection` fallback / `connectorId` extraction
 * identically.
 */

import { useMemo } from 'react';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { isValueSegmentArray, segmentsToString } from '@/types/valueSegment';
import { isManagedApiConnector, toShortManagedApiId } from '@/utils/connections';
import type { ConnectionReference } from '@/types/connectionReference';

export interface NodeDynamicDataContext {
  /** Current node id (empty string when no node is open). */
  nodeId: string;
  /** Parameter values from the node editor draft, with segment arrays collapsed to strings. */
  parameterValues: Record<string, unknown>;
  /** The connection reference the dynamic call should use, if any. */
  connectionReference: ConnectionReference | undefined;
  /**
   * Connector ID for the active connection. For managed APIs this is
   * normalized to the short `/managedApis/{name}` form expected by the
   * data-plane connector APIs.
   */
  connectorId: string;
}

/** Resolve the active connection reference for the node currently being edited. */
function resolveConnectionReference(
  nodeId: string,
  parameterValues: Record<string, unknown>,
  mapping: Record<string, string | null>,
  references: Record<string, ConnectionReference>,
): ConnectionReference | undefined {
  // Primary source: the explicit node→reference mapping.
  const mapped = mapping[nodeId];
  if (mapped && references[mapped]) return references[mapped];

  // Fallback: the in-progress `_connection` value held in the editor draft.
  // Used right after a workflow loads (before the mapping rehydrates) and
  // for tool nodes that don't sit in the mapping at all.
  const draft = parameterValues['_connection'];
  if (typeof draft === 'string' && references[draft]) return references[draft];

  return undefined;
}

export function useNodeDynamicDataContext(): NodeDynamicDataContext {
  const node = useNodeEditorStore((state) => state.node);
  const rawParameterValues = useNodeEditorStore((state) => state.parameterValues);
  const mapping = useConnectionStore((state) => state.mapping);
  const references = useConnectionStore((state) => state.references);

  const nodeId = node?.id ?? '';

  const parameterValues = useMemo<Record<string, unknown>>(() => {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(rawParameterValues)) {
      out[key] = isValueSegmentArray(value) ? segmentsToString(value) : value;
    }
    return out;
  }, [rawParameterValues]);

  const connectionReference = useMemo(
    () => resolveConnectionReference(nodeId, parameterValues, mapping, references),
    [nodeId, parameterValues, mapping, references],
  );

  const connectorId = useMemo(() => {
    const fromReference = connectionReference?.api?.id;
    const fromNodeData = (node?.nodeData as { connectorId?: string } | undefined)?.connectorId;
    const id = fromReference ?? fromNodeData ?? '';
    // Normalize managed-API IDs so downstream calls hit the canonical
    // `/managedApis/{name}` path regardless of which form was stored.
    return isManagedApiConnector(id) ? toShortManagedApiId(id) : id;
  }, [connectionReference, node]);

  return { nodeId, parameterValues, connectionReference, connectorId };
}
